/* eslint-disable @typescript-eslint/no-floating-promises */
import { RouterProvider } from "@tanstack/react-router";
import React from "react";
import ReactDOM from "react-dom/client";
import { router } from "./router.ts";
import "./styles/tailwind.css";
import "./styles/carouselMVP.css";
import "./styles/reactPlayerCustomStyles.scss";
import "./common/i18n";
import "@vidstack/react/player/styles/base.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { defineCustomElements } from "@ionic/pwa-elements/loader";
import { AuthProvider } from "@/context/AuthProvider";
import { StorageProvider } from "@/context/StorageProvider.tsx";
import { Capacitor } from "@capacitor/core";
import { LiveUpdate } from "@capawesome/capacitor-live-update";
import { checkForAppUpdate } from "@/utils/appUpdateService";
import OneSignal from "onesignal-cordova-plugin";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
// import { AppUpdateDebugWrapper } from "@/components/AppUpdateDebugger";
// import { VideoDownloadDebugWrapper } from "@/components/VideoDownloadDebugger";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { ErrorBoundaryFallback } from "@/components/ErrorBoundaryFallback";
import { version } from "../package.json";
import { detectOrphanedDownloads } from "@/utils/videoDownloadService";
import { SplashScreen } from "@capacitor/splash-screen";

// ============================================================================
// SENTRY INITIALIZATION WITH NATIVE CRASH REPORTING
// ============================================================================
const isDevelopment = import.meta.env.MODE === "development";
const isStaging = import.meta.env.MODE === "staging";

// Initialize Sentry with native crash reporting support (disabled in development)
if (!isDevelopment) {
	Sentry.init({
		// Włączamy szczegółowe logi Sentry, aby zobaczyć co się dzieje
		debug: false,
		tunnel: `https://${import.meta.env.VITE_DOMAIN_URL}/sentry-tunnel`,

		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		dsn:
			import.meta.env["VITE_SENTRY_DSN"] ||
			"https://7e9d4108b7b16407a0e11271a5f5ed92@o4510221459718144.ingest.de.sentry.io/4510221461880912",

		// Environment configuration
		environment: import.meta.env.MODE || "development",

		// Release tracking
		release: `ltca-mobile-${version}`,

		// ===== NATIVE BRIDGE CONFIGURATION =====
		// Enable native crash reporting (Android/iOS)
		enableNative: true,
		enableNativeCrashHandling: true,
		enableAutoSessionTracking: true,
		enableNativeNagger: false, // Disable console warnings about native SDK

		// Integrations (React web features)
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				// Mask all text content by default
				maskAllText: true,
				blockAllMedia: true,
			}),
		],

		// Performance Monitoring
		tracesSampleRate: isStaging ? 0.5 : 0.1,

		// Session Replay
		replaysSessionSampleRate: isStaging ? 0.2 : 0.1,
		replaysOnErrorSampleRate: 1.0, // Always capture replay on error

		// Ignore specific errors
		ignoreErrors: [
			// Browser extensions
			"top.GLOBALS",
			// Network errors that are expected
			"Network request failed",
			"NetworkError",
			// OneSignal errors (non-critical)
			/OneSignal/i,
		],

		// Configure tracing
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/akademialtca\.pl/,
			/^https:\/\/.*\.akademialtca\.pl/,
		],
	});

	console.log(
		`[Sentry] Initialized for ${import.meta.env.VITE_BUILD_ENV || "development"} environment with native crash reporting`
	);
} else {
	console.log("[Sentry] Disabled in development mode");
}

void defineCustomElements(window);

// ============================================================================
// LIVE UPDATE INITIALIZATION
// ============================================================================

/**
 * Initialize LiveUpdate SDK early in the app lifecycle.
 * Should be called as soon as possible, before any other Live Update methods.
 * Implements versioned channels with platform and environment detection.
 */
async function initializeLiveUpdate(): Promise<void> {
	// Only initialize on native platforms
	if (!Capacitor.isNativePlatform()) {
		console.log("[LiveUpdate] Skipping on web platform");
		return;
	}

	try {
		console.log("[LiveUpdate] Initializing...");
		await LiveUpdate.ready();
		console.log("[LiveUpdate] ✅ Initialized successfully");

		// Get platform (android/ios)
		const platform = Capacitor.getPlatform();
		console.log(`[LiveUpdate] Platform detected: ${platform}`);

		// Get native version code
		const { versionCode } = await LiveUpdate.getVersionCode(); // getVersionCode zwraca build czyli wersję techniczną, versionName to wersja marketingowa (!)
		console.log(`[LiveUpdate] Native version code: ${versionCode}`);

		// Determine environment based on app name or other indicators
		// In native builds, app name differs per environment
		let environment = "production";

		// Try to get app info to determine environment
		try {
			// Check if we're in development mode (Vite environment variable)
			const buildEnv = import.meta.env.MODE || "development";

			if (isStaging) {
				environment = "staging";
			} else if (isDevelopment) {
				environment = "development";
			} else {
				environment = "production";
			}

			console.log(`[LiveUpdate] Build environment detected: ${buildEnv}`);
		} catch (error) {
			console.warn(
				"[LiveUpdate] Could not determine environment, defaulting to production:",
				error
			);
		}

		console.log(`[LiveUpdate] Environment: ${environment}`);

		// Build channel name: platform-environment-versionCode
		const channel = `${platform}-${environment}-${versionCode}`;
		console.log(`[LiveUpdate] Syncing with channel: ${channel}`);

		// Sync with the channel (range-based versioning: min only, no max)
		let result;
		try {
			result = await LiveUpdate.sync({ channel });
		} catch (error) {
			console.log(`[LiveUpdate] Failed to sync: ${error}`);
			await SplashScreen.hide();
			console.log("[LiveUpdate] Failed to sync:", error);
		}

		// Log sync result
		console.log("[LiveUpdate] Sync completed:", {
			channel,
			result,
		});

		// FORCE UPDATE STRATEGY: Check if update was downloaded
		if (result.nextBundleId) {
			console.log("[LiveUpdate] Update downloaded, reloading app...");
			console.log("[LiveUpdate] Next bundle ID:", result.nextBundleId);

			// Reload app with new bundle BEFORE hiding splash screen
			await LiveUpdate.reload();
			// Note: reload() will restart the app, code after this won't execute
		} else {
			console.log("[LiveUpdate] No update available, hiding splash screen");

			// Hide native splash screen only if no update
			if (Capacitor.isNativePlatform()) {
				try {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
					await SplashScreen.hide();
				} catch (splashError: unknown) {
					console.warn(
						"[LiveUpdate] Could not hide splash screen:",
						splashError
					);
				}
			}

			// Check for app store update after splash screen is hidden
			// Delay to avoid blocking app startup
			setTimeout(() => {
				checkForAppUpdate().catch((error) => {
					console.error("[AppUpdate] Failed to check for update:", error);
				});
			}, 2000);
		}
	} catch (error: unknown) {
		console.error("[LiveUpdate] Failed to initialize:", error);

		// Even on error, try to hide splash screen to avoid stuck state
		if (Capacitor.isNativePlatform()) {
			try {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
				await SplashScreen.hide();
			} catch (splashError: unknown) {
				console.error(
					"[LiveUpdate] Failed to hide splash screen:",
					splashError
				);
			}
		}
	}
}

// Initialize LiveUpdate immediately (non-blocking)
initializeLiveUpdate();

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			networkMode: "always",
		},
		mutations: {
			networkMode: "always",
		},
	},
});

// ============================================================================
// ONESIGNAL INITIALIZATION
// ============================================================================

/**
 * Initialize OneSignal SDK early in the app lifecycle.
 * Event listeners for notifications are handled in AppNavigationListener component.
 */
function initializeOneSignal(): void {
	try {
		console.log("[OneSignal] Starting initialization...");

		// Check if offline - skip initialization gracefully
		if (!navigator.onLine) {
			console.log("[OneSignal] Offline detected, deferring initialization");
			// Setup listener to retry when online
			window.addEventListener(
				"online",
				() => {
					console.log("[OneSignal] Network restored, initializing...");
					try {
						initializeOneSignal();
					} catch (error: unknown) {
						console.error(
							"[OneSignal] Initialization failed after network restore:",
							error
						);
					}
				},
				{ once: true }
			);
			return;
		}

		OneSignal.Debug.setLogLevel(6);
		OneSignal.initialize(import.meta.env.VITE_ONESIGNAL_APP_ID);
		console.log("[OneSignal] Initialized successfully");
	} catch (error) {
		console.error("[OneSignal] Initialization failed:", error);
		// App continues without push notifications - not critical
	}
}

// Initialize native platform features
// These are registered BEFORE OneSignal to ensure they work even if OneSignal fails
if (Capacitor.isNativePlatform()) {
	// Initialize OneSignal (non-blocking)
	if (import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true") {
		initializeOneSignal();
	}
}

// Detect and fix interrupted downloads
detectOrphanedDownloads().catch((error) => {
	console.error("[VideoDownload] Failed to detect orphaned downloads:", error);
});

// ============================================================================
// SERVICE WORKER READINESS CHECK - DISABLED (kept for future reference)
// ============================================================================
// NOTE: This code is commented out because Live Update force update strategy
// now manages app initialization and splash screen. Service Worker readiness
// is no longer a blocking requirement for app startup.
//
// If you need to re-enable SW readiness check in the future, uncomment the
// function below and the call around line ~417.
// ============================================================================

// ============================================================================
// COMMENTED OUT - Service Worker readiness check function
// ============================================================================
// This function is disabled because Live Update force update strategy now
// manages app initialization. Re-enable by uncommenting the function below.
// ============================================================================
/*
async function waitForServiceWorkerReady(): Promise<boolean> {
	const SW_READY_TIMEOUT = 5000;
	const START_TIME = Date.now();
	return new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			console.warn("[SW] Timeout waiting for SW - continuing anyway (5s)");
			resolve(false);
		}, SW_READY_TIMEOUT);
		if (!("serviceWorker" in navigator)) {
			console.log("[SW] Service Worker not supported - continue");
			clearTimeout(timeoutId);
			resolve(false);
			return;
		}
		navigator.serviceWorker.ready
			.then((registration) => {
				const elapsed = Date.now() - START_TIME;
				console.log(`[SW] ✅ Service Worker ready (${elapsed}ms)`, {
					active: !!registration.active,
					waiting: !!registration.waiting,
					installing: !!registration.installing,
				});
				clearTimeout(timeoutId);
				resolve(true);
			})
			.catch((error) => {
				console.error("[SW] Error checking SW readiness:", error);
				clearTimeout(timeoutId);
				resolve(false);
			});
		if ("serviceWorker" in navigator) {
			const messageHandler = (event: MessageEvent) => {
				if (event.data?.type === "SW_READY") {
					console.log("[SW] Received SW_READY message from Service Worker");
					navigator.serviceWorker.controller?.removeEventListener("message", messageHandler);
					clearTimeout(timeoutId);
					resolve(true);
				}
			};
			navigator.serviceWorker.addEventListener("message", messageHandler);
			if (navigator.serviceWorker.controller) {
				navigator.serviceWorker.controller.postMessage({
					type: "CHECK_SW_STATUS",
				});
			}
		}
	});
}
*/

const rootElement = document.querySelector("#root") as Element;
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	console.log(
		"[App] Rendering main app immediately (Live Update handles initialization)"
	);

	root.render(
		<React.StrictMode>
			<SentryReact.ErrorBoundary
				fallback={(args) => (
					<ErrorBoundaryFallback
						error={
							args.error instanceof Error
								? args.error
								: new Error(String(args.error))
						}
						componentStack={args.componentStack ?? null}
						eventId={args.eventId ?? null}
						resetError={() => {
							args.resetError();
						}}
					/>
				)}
				showDialog={false}
			>
				<AuthProvider>
					<StorageProvider>
						<QueryClientProvider client={queryClient}>
							{/* <VideoDownloadDebugWrapper>
								<AppUpdateDebugWrapper> */}
							<RouterProvider router={router} />
							<ReactQueryDevtools initialIsOpen={false} />
							{/* </AppUpdateDebugWrapper>
							</VideoDownloadDebugWrapper> */}
						</QueryClientProvider>
					</StorageProvider>
				</AuthProvider>
			</SentryReact.ErrorBoundary>
		</React.StrictMode>
	);

	// Service Worker status logging (non-blocking)
	if ("serviceWorker" in navigator) {
		navigator.serviceWorker.ready
			.then(() => {
				console.log("[SW] Service Worker is active (non-blocking check)");
			})
			.catch((error) => {
				console.warn("[SW] Service Worker not available:", error);
			});
	}
}
